/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Alert, Spinner, Form, InputGroup } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';

const DriverEmailLog = () => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullEmailModal, setShowFullEmailModal] = useState(false);
  const [selectedEmailContent, setSelectedEmailContent] = useState(null);
  const [filter, setFilter] = useState({
    emailType: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    hasMore: false
  });

  // Load email logs for the current driver
  const loadEmailLogs = async () => {
    try {
      console.log('🔄 Loading driver email logs...');
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filter
      });
      
      console.log('📡 Making API call to:', `/api/user/email-logs?${params}`);
      const response = await axios.get(`/api/user/email-logs?${params}`);
      console.log('📧 API Response:', response.data);
      
      if (response.data.success) {
        setEmailLogs(response.data.logs);
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
          hasMore: response.data.hasMore
        }));
        console.log('✅ Email logs loaded:', response.data.logs.length, 'logs');
      }
    } catch (error) {
      console.error('❌ Error loading email logs:', error);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  // Show full email content
  const showFullEmailContent = (emailLog) => {
    setSelectedEmailContent({
      subject: emailLog.subject,
      recipientEmail: emailLog.recipientEmail,
      recipientName: emailLog.recipientName,
      driverName: emailLog.driverName,
      sentAt: emailLog.sentAt,
      status: emailLog.status,
      content: emailLog.fullContent || emailLog.contentPreview
    });
    setShowFullEmailModal(true);
  };

  // Apply filters
  const applyFilter = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadEmailLogs();
  };

  // Clear filters
  const clearFilters = () => {
    setFilter({
      emailType: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Load data on component mount
  useEffect(() => {
    loadEmailLogs();
  }, [pagination.page]);

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4>📧 My Email Log</h4>
              <p className="text-muted mb-0">View all emails sent from your account</p>
            </Card.Header>
            <Card.Body>
              {/* Filter Section */}
              <Card className="mb-4">
                <Card.Header>
                  <h6>Email Log Filter</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Email Type</Form.Label>
                        <Form.Select
                          value={filter.emailType}
                          onChange={(e) => setFilter(prev => ({ ...prev, emailType: e.target.value }))}
                        >
                          <option value="">All Types</option>
                          <option value="diary">Diary</option>
                          <option value="booking_confirmation">Booking Confirmation</option>
                          <option value="lead_notification">Lead Notification</option>
                          <option value="welcome">Welcome</option>
                          <option value="test">Test</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={filter.status}
                          onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                        >
                          <option value="">All Status</option>
                          <option value="sent">Sent</option>
                          <option value="failed">Failed</option>
                          <option value="pending">Pending</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={filter.startDate}
                          onChange={(e) => setFilter(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={filter.endDate}
                          onChange={(e) => setFilter(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <div className="d-flex gap-2 w-100">
                        <Button variant="primary" onClick={applyFilter} size="sm">
                          Apply
                        </Button>
                        <Button variant="outline-secondary" onClick={clearFilters} size="sm">
                          Clear
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Email Logs Table */}
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Email Logs ({pagination.total} total)</h6>
                    <Button variant="outline-primary" size="sm" onClick={loadEmailLogs}>
                      Refresh
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="text-center">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading email logs...</p>
                    </div>
                  ) : emailLogs.length === 0 ? (
                    <Alert variant="info">
                      No email logs found. Try adjusting your filters or check back later.
                    </Alert>
                  ) : (
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Date & Time</th>
                          <th>Recipient</th>
                          <th>Subject</th>
                          <th>Type</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log) => (
                          <tr 
                            key={log._id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => showFullEmailContent(log)}
                            className="email-log-row"
                          >
                            <td>
                              <div>
                                <strong>{new Date(log.sentAt).toLocaleDateString()}</strong>
                                <br />
                                <small className="text-muted">
                                  {new Date(log.sentAt).toLocaleTimeString()}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                {log.recipientName && (
                                  <strong>{log.recipientName}</strong>
                                )}
                                <br />
                                <small className="text-muted">{log.recipientEmail}</small>
                              </div>
                            </td>
                            <td>
                              <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {log.subject}
                              </div>
                            </td>
                            <td>
                              <Badge bg="secondary">{log.emailType}</Badge>
                            </td>
                            <td>
                              <Badge bg={log.status === 'sent' ? 'success' : log.status === 'failed' ? 'danger' : 'warning'}>
                                {log.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {/* Pagination */}
                  {pagination.total > pagination.limit && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                      </div>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          disabled={pagination.page === 1}
                          onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        >
                          Previous
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          disabled={!pagination.hasMore}
                          onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Full Email Content Modal */}
      <Modal show={showFullEmailModal} onHide={() => setShowFullEmailModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Email Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmailContent && (
            <div>
              <div className="mb-3">
                <h5>{selectedEmailContent.subject}</h5>
                <div className="row">
                  <div className="col-md-6">
                    <strong>From:</strong> {selectedEmailContent.driverName}
                  </div>
                  <div className="col-md-6">
                    <strong>To:</strong> {selectedEmailContent.recipientName || selectedEmailContent.recipientEmail}
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <strong>Sent:</strong> {new Date(selectedEmailContent.sentAt).toLocaleString()}
                  </div>
                  <div className="col-md-6">
                    <strong>Status:</strong> 
                    <Badge bg={selectedEmailContent.status === 'sent' ? 'success' : selectedEmailContent.status === 'failed' ? 'danger' : 'warning'} className="ms-2">
                      {selectedEmailContent.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <hr />
              
              <div className="email-content-container">
                <h6>Email Content:</h6>
                <div 
                  className="border p-3 bg-light"
                  style={{ 
                    maxHeight: '500px', 
                    overflow: 'auto',
                    minHeight: '200px'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedEmailContent.content }}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFullEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" />
    </Container>
  );
};

export default DriverEmailLog;
